// Drone Ops write-back helpers. Mirror of dispatchActions.ts in structure:
// update the deals row (source of truth) + insert an audit row in
// dispatch_schedule. A webhook fires for homeowner ETA notifications.

import { supabase, SUPABASE_URL } from "./supabase";
import type { Deal } from "./types";

const WEBHOOK_URL =
  (import.meta.env.VITE_DISPATCH_WEBHOOK_URL as string | undefined) ||
  `${SUPABASE_URL}/functions/v1/dispatch-webhook`;

async function fireWebhook(payload: Record<string, unknown>) {
  if (!WEBHOOK_URL) return;
  try {
    await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Drone webhook failed (non-blocking):", err);
  }
}

/** Claim + schedule a drone flight from the Queue tab.
 *
 *  Routes through the `schedule_drone_flight` RPC (SECURITY DEFINER) so it
 *  works even when the get_my_role() helper / pre-request hook fails to
 *  resolve the caller's role — observed Apr 27 2026 from Route Builder
 *  bulk-schedule on iPhone Safari. The RPC runs the deal UPDATE + the
 *  dispatch_schedule INSERT in one server-side transaction and verifies the
 *  caller via auth.uid() → auth.users.email → team_roster.
 */
export async function scheduleDrone(
  deal: Deal,
  params: { date: string; pilotId: string },
  actorId: string | null,
) {
  const { error } = await supabase.rpc("schedule_drone_flight", {
    p_deal_id: deal.id,
    p_pilot_id: params.pilotId,
    p_date: params.date,
    p_actor_id: actorId,
  });
  if (error) throw error;

  await fireWebhook({
    action: "schedule_drone",
    deal_id: deal.id,
    date: params.date,
    pilot_id: params.pilotId,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    actor_id: actorId,
  });
}

/** Send en-route text to homeowner. One-tap — no ETA prompt.
 *  Text reads: "Hi {name}, Sam, our drone pilot, will be there shortly
 *  to inspect your roof at {street}. No need to be home — he'll be
 *  working from the outside. Thank you!" */
export async function sendEnrouteText(
  deal: Deal,
  actorId: string | null,
) {
  const firstName = (deal.homeowner_name ?? "").split(/\s+/)[0] || "there";
  const street = deal.property_street ?? "your property";
  const message =
    `Hi ${firstName}, Sam, our drone pilot, will be there shortly ` +
    `to inspect your roof at ${street}. No need to be home — he'll be ` +
    `working from the outside. Thank you!`;

  await fireWebhook({
    action: "drone_enroute",
    deal_id: deal.id,
    message,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    property_street: deal.property_street,
    actor_id: actorId,
  });
}

/** Mark a drone flight complete. Advances stage, records IMGING link.
 *  Routes through the SECURITY DEFINER `complete_drone_flight` RPC for
 *  the same reason as `scheduleDrone` — see notes there. */
export async function completeDrone(
  deal: Deal,
  params: { imgingLink: string; notes: string },
  actorId: string | null,
) {
  const { error } = await supabase.rpc("complete_drone_flight", {
    p_deal_id: deal.id,
    p_imging_link: params.imgingLink || "",
    p_notes: params.notes || "",
    p_actor_id: actorId,
  });
  if (error) throw error;

  await fireWebhook({
    action: "drone_complete",
    deal_id: deal.id,
    imging_link: params.imgingLink,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    actor_id: actorId,
  });
}

/** Send a drone-scheduled deal back to the queue.
 *  Used when weather pushes a stop and the pilot needs to reschedule it.
 *  Routes through the SECURITY DEFINER `unschedule_drone_flight` RPC, which
 *  clears `drone_scheduled_date` + `assigned_drone_pilot_id`, sets stage back
 *  to `appointment_received`, and writes a `cancelled` audit row in
 *  `dispatch_schedule`. */
export async function unscheduleDrone(
  deal: Deal,
  params: { reason?: string },
  actorId: string | null,
) {
  const { error } = await supabase.rpc("unschedule_drone_flight", {
    p_deal_id: deal.id,
    p_reason: params.reason ?? null,
    p_actor_id: actorId,
  });
  if (error) throw error;

  await fireWebhook({
    action: "drone_unschedule",
    deal_id: deal.id,
    reason: params.reason ?? null,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    actor_id: actorId,
  });
}

/** Flag as unable-to-fly → branches to needs_climb_inspection.
 *  Routes through the SECURITY DEFINER `mark_drone_unable_to_fly` RPC. */
export async function markUnableToFly(
  deal: Deal,
  params: { reason: string },
  actorId: string | null,
) {
  const { error } = await supabase.rpc("mark_drone_unable_to_fly", {
    p_deal_id: deal.id,
    p_reason: params.reason,
    p_actor_id: actorId,
  });
  if (error) throw error;

  await fireWebhook({
    action: "drone_unable_to_fly",
    deal_id: deal.id,
    reason: params.reason,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    actor_id: actorId,
  });
}
